import React, { useEffect, useState } from 'react';
import { Skeleton, SkeletonText } from '../../components/ui/Skeleton';
import { useLocation, useNavigate } from 'react-router-dom';
import AccountLayout from './AccountLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PlanCard from './components/PlanCard';
import AlertService from '../../utils/alertService';
import billingService, { formatMxn } from '../../utils/billingService';

const AccountUpgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponPreview, setCouponPreview] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show cancel message returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'cancelled') {
      AlertService?.info?.('Checkout cancelado', 'No se realizó ningún cargo.', {
        autoClose: true,
      });
      navigate('/app/account/plans/upgrade', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes, usageRes] = await Promise.all([
        billingService.listPlans(),
        billingService.getMySubscription().catch(() => null),
        billingService.getEmployeeUsage().catch(() => null),
      ]);
      if (Array.isArray(plansRes?.data)) setPlans(plansRes.data);
      if (subRes?.data) setSubscription(subRes.data);
      if (usageRes?.data) setUsage(usageRes.data);
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async (planId) => {
    setCouponError(null);
    if (!couponCode) {
      setCouponPreview(null);
      return null;
    }
    try {
      setCouponLoading(true);
      const res = await billingService.validateCoupon({ code: couponCode, planId });
      if (!res?.valid) {
        setCouponPreview(null);
        setCouponError(res?.reason || 'Cupón inválido');
        return null;
      }
      setCouponPreview(res);
      return res;
    } catch (err) {
      setCouponPreview(null);
      setCouponError(err?.response?.data?.error?.message || 'No se pudo validar el cupón');
      return null;
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSubmittingId(plan.id);

    const isCurrent = subscription?.plan?.id === plan.id;
    if (isCurrent) {
      setSubmittingId(null);
      return;
    }

    const isPlanChange = subscription?.stripe_subscription_id;
    // El trial sólo aplica en la PRIMERA suscripción vía Stripe (mismo criterio
    // que el backend: plan.trial_days > 0 y sin stripe_subscription_id previo).
    const trialDays = !isPlanChange && Number(plan.trial_days) > 0 ? Number(plan.trial_days) : 0;
    const confirmTitle = isPlanChange ? '¿Cambiar de plan?' : '¿Suscribirte a este plan?';
    const isTrialing = subscription?.status === 'trialing';
    const confirmMsg = isPlanChange
      ? isTrialing && !(Number(plan.trial_days) > 0)
        ? `Pasarás al plan "${plan.name}" por ${formatMxn(plan.price_mxn)}/mes. Tu periodo de prueba TERMINA al confirmar y el cobro inicia de inmediato.`
        : `Pasarás al plan "${plan.name}" por ${formatMxn(plan.price_mxn)}/mes. Los cambios se aplican inmediatamente y se prorratea el costo restante del período.`
      : trialDays > 0
        ? `Te suscribirás al plan "${plan.name}" con ${trialDays} días de prueba gratis; después se cobrarán ${formatMxn(plan.price_mxn)}/mes. Te redirigiremos a Stripe para capturar tu método de pago.`
        : `Te suscribirás al plan "${plan.name}" por ${formatMxn(plan.price_mxn)}/mes. Te redirigiremos a Stripe para capturar tu método de pago.`;

    const confirmed = await AlertService?.confirm(confirmTitle, confirmMsg, {
      confirmText: isPlanChange ? 'Cambiar plan' : 'Continuar a pago',
      cancelText: 'Cancelar',
    });
    if (!confirmed) {
      setSubmittingId(null);
      return;
    }

    try {
      if (isPlanChange) {
        // In-place plan change (already has a subscription)
        await billingService.changePlan({ planId: plan.id });
        await AlertService?.success(
          '¡Plan actualizado!',
          'Tu nuevo plan ya está activo. Stripe ajustará el siguiente cobro con la diferencia.',
          { autoClose: true },
        );
        navigate('/app/account/plan');
      } else {
        // First-time subscription via Checkout
        const successUrl = `${window.location.origin}/app/account/plan?checkout=success&session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${window.location.origin}/app/account/plans/upgrade?checkout=cancelled`;

        const promoCode = couponPreview?.code || (couponCode || undefined);
        const { url } = await billingService.createCheckoutSession({
          planId: plan.id,
          successUrl,
          cancelUrl,
          promotionCode: promoCode,
          allowPromotionCodes: !promoCode,
        });
        if (url) window.location.href = url;
      }
    } catch (err) {
      AlertService?.error(
        'No se pudo procesar',
        err?.response?.data?.error?.message || err?.message || 'Inténtalo de nuevo.',
      );
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <AccountLayout
      title="Elige tu plan"
      subtitle="Los precios incluyen IVA. Puedes cambiar de plan cuando quieras; los cambios se aplican inmediatamente."
    >
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 space-y-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-9 w-24" />
              <SkeletonText lines={3} />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Coupon entry */}
          <div className="a2-card p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium a2-t-body mb-1">
                  ¿Tienes un cupón? Ingresa el código antes de seleccionar tu plan.
                </label>
                <Input
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setCouponPreview(null);
                    setCouponError(null);
                  }}
                  placeholder="EJ: PROMO2026"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => validateCoupon(subscription?.plan?.id)}
                disabled={couponLoading || !couponCode}
              >
                {couponLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Validando...
                  </>
                ) : (
                  <>
                    <Icon name="Tag" size={16} className="mr-2" />
                    Validar
                  </>
                )}
              </Button>
            </div>
            {couponError && (
              <p className="text-sm a2-t-danger mt-2 flex items-center gap-1">
                <Icon name="XCircle" size={14} />
                {couponError}
              </p>
            )}
            {couponPreview && (
              <p className="text-sm a2-t-ok mt-2 flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                Cupón válido:{' '}
                {couponPreview.discount_type === 'percentage'
                  ? `${couponPreview.percent_off}% de descuento`
                  : `${formatMxn(couponPreview.amount_off)} de descuento`}{' '}
                ({couponPreview.duration === 'forever'
                  ? 'permanente'
                  : couponPreview.duration === 'repeating'
                  ? `por ${couponPreview.duration_in_months} meses`
                  : 'por única vez'}
                )
              </p>
            )}
          </div>

          {/* Plan grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const isCurrent = subscription?.plan?.id === plan.id;
              const isRecommended =
                !!usage?.isOverage && usage?.recommendedPlan?.id === plan.id && !isCurrent;
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={isCurrent}
                  isRecommended={isRecommended}
                  loading={submittingId === plan.id}
                  onSelect={() => handleSelectPlan(plan)}
                  ctaLabel={subscription?.stripe_subscription_id ? 'Cambiar a este plan' : 'Elegir este plan'}
                />
              );
            })}
          </div>

          {/* FAQ / Notes */}
          <div className="a2-surface-2 rounded-xl p-5 text-sm a2-t-body space-y-2">
            <p>
              <strong>Notas:</strong>
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Los precios mostrados ya incluyen IVA.</li>
              <li>Puedes cambiar a un plan superior o inferior cuando quieras.</li>
              <li>Las cancelaciones surten efecto al final del período pagado; no hay reembolsos.</li>
              <li>
                Si superas el límite de empleados de tu plan, solo verás los primeros N empleados
                hasta que actualices.
              </li>
            </ul>
          </div>
        </div>
      )}
    </AccountLayout>
  );
};

export default AccountUpgrade;
